import { createSelector } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import { PaymentType } from './models';
import { baseApi } from '@centerpiece/components';

export const payment_type = baseApi
  .enhanceEndpoints({ addTagTypes: ['PaymentTypes', 'PaymentType'] })
  .injectEndpoints({
    endpoints: builder => ({
      getPaymentTypes: builder.query<PaymentType[], { params?: object }>({
        query: ({ params }: { params?: object }) => ({
          url: 'payment_type',
          method: 'GET',
          params: params,
        }),
        providesTags: (result, error, arg) =>
          result
            ? [
                ...result.map(({ id }) => ({
                  type: 'PaymentType' as const,
                  id,
                })),
                'PaymentTypes',
              ]
            : ['PaymentTypes'],
      }),
      getPaymentType: builder.query<PaymentType, string>({
        query: id => ({
          url: `payment_type/${id}`,
          method: 'GET',
        }),
        providesTags: (result, error, arg) => [
          { type: 'PaymentType', id: arg },
        ],
        // configuration for an individual endpoint, overriding the api setting
        keepUnusedDataFor: 90,
      }),
      createPaymentType: builder.mutation({
        query: request => ({
          url: 'payment_type',
          method: 'POST',
          body: request,
        }),
        invalidatesTags: ['PaymentTypes'],
      }),
      updatePaymentType: builder.mutation<PaymentType, PaymentType>({
        query: request => ({
          url: `payment_type/${request.id}`,
          method: 'PUT',
          body: request,
        }),
        invalidatesTags: (result, error, arg) => [
          { type: 'PaymentType', id: arg.id },
        ],
        async onQueryStarted({ id, ...patch }, { dispatch, queryFulfilled }) {
          const patchResult = dispatch(
            payment_type.util.updateQueryData(
              'getPaymentType',
              `${id}`,
              draft => {
                Object.assign(draft, patch);
              }
            )
          );
          try {
            await queryFulfilled;
          } catch {
            patchResult.undo();

            /**
             * Alternatively, on failure you can invalidate the corresponding cache tags
             * to trigger a re-fetch:
             * dispatch(payment_type.util.invalidateTags(['PaymentTypes']))
             */
            dispatch(payment_type.util.invalidateTags(['PaymentTypes']));
          }
        },
      }),
      deletePaymentType: builder.mutation<PaymentType, number>({
        query: id => ({
          url: `payment_type/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: (result, error, arg) =>
          result
            ? [{ type: 'PaymentType' as const, arg }, 'PaymentTypes']
            : ['PaymentTypes'],
      }),
    }),
  });

// Auto-generated hooks
export const {
  useGetPaymentTypesQuery,
  useGetPaymentTypeQuery,
  useCreatePaymentTypeMutation,
  useUpdatePaymentTypeMutation,
  useDeletePaymentTypeMutation,
  usePrefetch: usePaymentTypesPrefetch,
} = payment_type;
const selectPaymentTypesRoot = payment_type.endpoints.getPaymentTypes.select(
  {}
);
export const selectPaymentTypes = createSelector(
  selectPaymentTypesRoot,
  payment_type => payment_type.data || []
);
